import {useCallback} from 'react'
import {toPng,toSvg} from 'html-to-image'
import {getRectOfNodes,getTransformForBounds} from 'reactflow'

// Mermaid nodeType → shape syntax helpers
const NODE_TO_MERMAID={
    rectNode:      (id,text) => `${id}["${text}"]`,
    roundedNode:   (id,text) => `${id}("${text}")`,
    squircleNode:  (id,text) => `${id}("${text}")`,
    circleNode:    (id,text) => `${id}(("${text}"))`,
    diamondNode:   (id,text) => `${id}{"${text}"}`,
    hexNode:       (id,text) => `${id}{{"${text}"}}`,
    cylinderNode:  (id,text) => `${id}[("${text}")]`,
    arrowNode:     (id,text) => `${id}>"${text}"]`,
    skewRectNode:  (id,text) => `${id}[/"${text}"/]`,
    trapNode:      (id,text) => `${id}[/"${text}"\\]`,
    refTrapNode:   (id,text) => `${id}[\\"${text}"/]`,
    arrowRectNode: (id,text) => `${id}[["${text}"]]`,
    diamondRectNode:(id,text) => `${id}{"${text}"}`,
    cloudNode:     (id,text) => `${id}("${text}")`,
    triangleNode:  (id,text) => `${id}["${text}"]`,
    pentNode:      (id,text) => `${id}["${text}"]`,
    hexNodeShape:  (id,text) => `${id}{{"${text}"}}`,
    dNode:         (id,text) => `${id}("${text}")`,
    tapeNode:      (id,text) => `${id}["${text}"]`,
    crossNode:     (id,text) => `${id}["${text}"]`,
    horCylinderNode:(id,text) => `${id}[("${text}")]`,
    refTrapNode2:  (id,text) => `${id}["${text}"]`,
    textNode:      (id,text) => `${id}["${text}"]`,
    noteNode:      (id,text) => `${id}["${text}"]`,
}

function nodesToMermaid(nodes,edges) {
    // Sanitize ID for Mermaid (replace non-alphanum with _)
    const mid=(id) => 'n_'+id.replace(/[^a-zA-Z0-9]/g,'_')
    const escape=(t) => (t||'').replace(/"/g,"'")

    const lines=['flowchart TD']

    // Node definitions
    nodes.filter(n => n.type!=='reccNode').forEach(node => {
        const id=mid(node.id)
        const text=escape(node.data?.text||'')
        const shapeFn=NODE_TO_MERMAID[node.type]
        if(shapeFn) {
            lines.push('    '+shapeFn(id,text))
        } else {
            lines.push(`    ${id}["${text}"]`)
        }
    })

    // Edge definitions
    edges.forEach(edge => {
        const src=mid(edge.source)
        const tgt=mid(edge.target)
        const label=edge.label? `|"${escape(String(edge.label))}"|`:`>`
        const arrow=edge.markerEnd?.color==='rgba(0,0,0,0)'? `---`:`-->`
        if(edge.label) {
            lines.push(`    ${src} ${arrow}${label} ${tgt}`)
        } else {
            lines.push(`    ${src} ${arrow} ${tgt}`)
        }
    })

    return lines.join('\n')
}

function downloadFile(content,filename,mimeType) {
    const blob=new Blob([content],{type: mimeType})
    const url=URL.createObjectURL(blob)
    const a=document.createElement('a')
    a.href=url
    a.download=filename
    a.click()
    URL.revokeObjectURL(url)
}

export default function useExport({getNodes,title='flowchart'}) {
    const exportMermaid=useCallback((nodes,edges) => {
        const mmd=nodesToMermaid(nodes,edges)
        downloadFile(mmd,`${title}.mmd`,'text/plain')
    },[title])

    const exportJSON=useCallback((nodes,edges) => {
        const persistable=nodes.filter(n => n.type!=='reccNode')
        const data=JSON.stringify({
            nodes: persistable.map(n => ({...n,selected: false})),
            edges: edges.map(e => ({...e,selected: false})),
            title,
            exportedAt: new Date().toISOString()
        },null,2)
        downloadFile(data,`${title}.json`,'application/json')
    },[title])

    const importJSON=useCallback((file,{setNodes,setEdges,setTitle}) => {
        const reader=new FileReader()
        reader.onload=(e) => {
            try {
                const data=JSON.parse(e.target.result)
                if(data.nodes) setNodes(data.nodes)
                if(data.edges) setEdges(data.edges)
                if(data.title&&setTitle) setTitle(data.title)
            } catch {
                // silently ignore malformed JSON
            }
        }
        reader.readAsText(file)
    },[])

    const exportPNG=useCallback(async (scale=1) => {
        const viewport=document.querySelector('.react-flow__viewport')
        if(!viewport) return

        const allNodes=getNodes()
        const imageWidth=Math.round(1280*scale)
        const imageHeight=Math.round(960*scale)
        const nodesBounds=getRectOfNodes(allNodes)
        const transform=getTransformForBounds(nodesBounds,imageWidth,imageHeight,0.5,2)

        const dataUrl=await toPng(viewport,{
            backgroundColor: '#d8f3df',
            width: imageWidth,
            height: imageHeight,
            pixelRatio: scale,
            style: {
                width: imageWidth,
                height: imageHeight,
                transform: `translate(${transform[0]}px,${transform[1]}px) scale(${transform[2]})`,
            }
        })
        downloadFile(atob(dataUrl.split(',')[1]),`${title}.png`,'image/png')
        const a=document.createElement('a')
        a.href=dataUrl
        a.download=`${title}.png`
        a.click()
    },[getNodes,title])

    const exportSVG=useCallback(async () => {
        const viewport=document.querySelector('.react-flow__viewport')
        if(!viewport) return

        const allNodes=getNodes()
        const imageWidth=1280
        const imageHeight=960
        const nodesBounds=getRectOfNodes(allNodes)
        const transform=getTransformForBounds(nodesBounds,imageWidth,imageHeight,0.5,2)

        const svgString=await toSvg(viewport,{
            backgroundColor: '#d8f3df',
            width: imageWidth,
            height: imageHeight,
            style: {
                width: imageWidth,
                height: imageHeight,
                transform: `translate(${transform[0]}px,${transform[1]}px) scale(${transform[2]})`,
            }
        })
        downloadFile(svgString,`${title}.svg`,'image/svg+xml')
    },[getNodes,title])

    return {exportMermaid,exportJSON,importJSON,exportPNG,exportSVG}
}
